// File: Pages/Reports/components/forms/FormComponents.jsx

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { CustomSelect } from '../../../../Pages/Teases/CustomSelect'
import styles from '../../index.module.css'

// Text Input Field Component
export const TextField = ({
  name,
  value,
  onChange,
  placeholder,
  disabled,
  required,
  error,
  label,
  subtitle
}) => {
  return (
    <div className={styles.formSection}>
      {label && (
        <h3 className={styles.sectionTitle}>
          {label} {required && <span className={styles.required}>*</span>}
        </h3>
      )}
      {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
      <div className={styles.formRow}>
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          className={styles.inputField}
          placeholder={placeholder}
          disabled={disabled}
        />
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    </div>
  )
}

// Text Area Component
export const TextArea = ({
  name,
  value,
  onChange,
  placeholder,
  disabled,
  required,
  error,
  label,
  subtitle
}) => {
  return (
    <div className={styles.formSection}>
      {label && (
        <h3 className={styles.sectionTitle}>
          {label} {required && <span className={styles.required}>*</span>}
        </h3>
      )}
      {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
      <div className={styles.formRow}>
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          className={`${styles.inputField} ${styles.textarea}`}
          placeholder={placeholder}
          disabled={disabled}
        />
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    </div>
  )
}

// Select Dropdown Component
export const SelectField = ({
  name,
  options,
  selectedOption,
  onOptionSelect,
  onSearch,
  disabled,
  isLoading,
  required,
  error,
  label,
  subtitle,
  placeholder,
  searchable,
  useCustomSelect
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  
  const handleSearch = (e) => {
    const term = e.target.value
    setSearchTerm(term)
    if (onSearch) {
      onSearch(e)
    }
  }
  
  // Format options for CustomSelect if they're objects
  const formattedOptions = options.map(option => 
    typeof option === 'string' ? option : (option.label || option.name || option.value)
  );
  
  return (
    <div className={styles.formSection}>
      {label && (
        <h3 className={styles.sectionTitle}>
          {label} {required && <span className={styles.required}>*</span>}
        </h3>
      )}
      {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
      <div className={styles.formRow}>
        {useCustomSelect ? (
          <CustomSelect
            options={formattedOptions}
            placeholder={placeholder || "Select an option"}
            defaultOption={selectedOption}
            onOptionSelect={value => {
              // Find the original option object if needed
              const originalOption = typeof options[0] !== 'string' 
                ? options.find(opt => (opt.label || opt.name) === value) 
                : value;
              onOptionSelect(originalOption || value);
            }}
            label={label}
            isRequired={required}
            disabled={disabled || isLoading}
          />
        ) : (
          <div className={styles.selectContainer}>
            <div 
              className={styles.selectButton}
              onClick={() => !disabled && setIsOpen(!isOpen)}
            >
              {selectedOption || <span className={styles.placeholder}>{placeholder || "Select an option"}</span>}
            </div>
            
            {isOpen && (
              <div className={styles.selectDropdown}>
                {searchable && (
                  <div className={styles.searchContainer}>
                    <input
                      type="text"
                      className={styles.searchInput}
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={handleSearch}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}
                
                <div className={styles.optionsContainer}>
                  {isLoading ? (
                    <div className={styles.loadingOption}>Loading...</div>
                  ) : options.length === 0 ? (
                    <div className={styles.noResults}>No options found</div>
                  ) : (
                    options.map((option, index) => {
                      // Support either string options or object options
                      const displayValue = typeof option === 'string' ? option : option.label || option.name
                      const isSelected = selectedOption === displayValue
                      
                      return (
                        <div
                          key={index}
                          className={`${styles.option} ${isSelected ? styles.selectedOption : ''}`}
                          onClick={() => {
                            onOptionSelect(option)
                            setIsOpen(false)
                            setSearchTerm('')
                          }}
                        >
                          {displayValue}
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    </div>
  )
}

// Checkbox Component
export const CheckboxField = ({
  name,
  checked,
  onChange,
  disabled,
  label,
  error
}) => {
  return (
    <div className={styles.formSection}>
      <div className={styles.formRow}>
        <div className={styles.checkboxContainer}>
          <input
            type="checkbox"
            id={name}
            name={name}
            className={styles.checkbox}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
          />
          <label htmlFor={name} className={styles.checkboxLabel}>
            {label}
          </label>
        </div>
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    </div>
  )
}

// Date Input Component
export const DateField = ({
  name,
  value,
  onChange,
  disabled,
  required,
  error,
  label,
  subtitle
}) => {
  return (
    <div className={styles.formSection}>
      {label && (
        <h3 className={styles.sectionTitle}>
          {label} {required && <span className={styles.required}>*</span>}
        </h3>
      )}
      {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
      <div className={styles.formRow}>
        <input
          type="date"
          name={name}
          value={value}
          onChange={onChange}
          className={styles.inputField}
          disabled={disabled}
        />
        {error && <p className={styles.errorText}>{error}</p>}
      </div>
    </div>
  )
}

// Multi-Checkbox Selection Component
export const MultiCheckboxField = ({
  options,
  selectedOptions,
  onToggle,
  onSearch,
  disabled,
  required,
  error,
  label,
  subtitle,
  isLoading,
  searchable,
  useCustomSelect
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  
  const handleSearch = (e) => {
    const term = e.target.value
    setSearchTerm(term)
    if (onSearch) {
      onSearch(term)
    }
  }
  
  // Format options for display
  const formattedOptions = options.map(option => 
    typeof option === 'string' ? option : option.label || option.name || option.value
  );
  
  // Get selected option labels for display
  const selectedLabels = selectedOptions.map(option => 
    typeof option === 'string' ? option : option.label || option.name || option.value
  );
  
  return (
    <div className={styles.formSection}>
      {label && (
        <h3 className={styles.sectionTitle}>
          {label} {required && <span className={styles.required}>*</span>}
        </h3>
      )}
      {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
      
      {searchable && (
        <div className={styles.searchContainer}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            disabled={isLoading || disabled}
          />
        </div>
      )}
      
      <div className={styles.formRow} style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }}>
        {isLoading ? (
          <p>Loading options...</p>
        ) : options.length === 0 ? (
          <p>No options found</p>
        ) : (
          options.map((option) => (
            <div key={option.value} className={styles.checkboxContainer}>
              <input
                type="checkbox"
                id={`option-${option.value}`}
                className={styles.checkbox}
                checked={selectedOptions.some(o => o.value === option.value)}
                onChange={() => onToggle(option)}
                disabled={disabled}
              />
              <label htmlFor={`option-${option.value}`} className={styles.checkboxLabel}>
                {option.label}
              </label>
            </div>
          ))
        )}
      </div>
      
      {error && <p className={styles.errorText}>{error}</p>}
    </div>
  )
}

// Submit Button Component
export const SubmitButton = ({ isSubmitting, label }) => {
  return (
    <div className={styles.buttonGroup}>
      <button
        type="submit"
        className={`${styles.faqCardLink}`} 
        disabled={isSubmitting}
        style={{ padding: '10px 20px', fontSize: '15px' }} 
      >
        {isSubmitting ? 'Submitting...' : label || 'Submit Report'}
      </button>
    </div>
  )
}

// Set PropTypes for all components
TextField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  subtitle: PropTypes.string
}

TextArea.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  subtitle: PropTypes.string
}

SelectField.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ])).isRequired,
  selectedOption: PropTypes.string,
  onOptionSelect: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  subtitle: PropTypes.string,
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  useCustomSelect: PropTypes.bool
}

CheckboxField.propTypes = {
  name: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.string.isRequired,
  error: PropTypes.string
}

DateField.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  subtitle: PropTypes.string
}

MultiCheckboxField.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  selectedOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  onToggle: PropTypes.func.isRequired,
  onSearch: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  label: PropTypes.string,
  subtitle: PropTypes.string,
  isLoading: PropTypes.bool,
  searchable: PropTypes.bool,
  useCustomSelect: PropTypes.bool
}

SubmitButton.propTypes = {
  isSubmitting: PropTypes.bool,
  label: PropTypes.string
}

// Export components as a collection
export const FormComponents = {
  TextField,
  TextArea,
  SelectField,
  CheckboxField,
  DateField,
  MultiCheckboxField,
  SubmitButton
}

export default FormComponents